import { applyRefractionActions } from '@ca/common'
import {
  Customer,
  DealType,
  OrderValues,
  PackageVariant,
  Store,
} from '@ca/common/types'
import FrameUtils from '@frame/FrameUtils'
import {
  BundleType,
  OrderFrame,
  SelectedPackage,
  StepMode,
  StepsState,
} from '@order/orderTypes'
import { Discounts } from '@order/utils/discounts/Discounts'
import { DEFAULT_FLOW_NAME } from '@order/utils/getFlow'
import { VariablesUtils, VariableValues } from '@order/utils/OrderVariables'
import { OrderPriceSummary } from '@order/utils/summary/getOrderPriceSummary'
import { differenceInYears, isValid, parseISO } from 'date-fns'
import { getRemoteConfig } from 'hooks/useRemoteConfigStore'
import useSession from 'hooks/useSession'
import { getAppRegion } from 'storage/useRemoteConfigParams'
import { OrderState, resolvePd } from '../state/orderState'
import { getDealsById, resolveSessionDeal } from '../useDeal'
import { useOrderState } from '../useOrderState'
import { getPrescriptionByIdFromCache } from '../usePrescription'

export const getAge = (birthday: Date | string | null | undefined) => {
  if (!birthday) {
    return null
  }
  const date = typeof birthday === 'string' ? parseISO(birthday) : birthday
  if (!isValid(date)) {
    return null
  }

  const age = differenceInYears(new Date(), date)
  return age
}

export const getPackageValues = (packageCfg?: SelectedPackage | null) => {
  return {
    ...packageCfg?.overrides,
    package: packageCfg?.value,
    packageName: packageCfg?.name,
    packageColor: packageCfg?.color,
    type: packageCfg?.type,
    lensPrefix: packageCfg?.lensPrefix,
  }
}

export const getValuesFromSteps = (steps: StepsState) =>
  Object.fromEntries(
    steps.flatMap((item) => [[item.var, item.val], ...(item.o || [])])
  )

export const withPackageVariantValues = ({
  variant,
  values,
  fullState,
}: {
  variant: PackageVariant | undefined | null
  values: OrderValues
  fullState: StepsState
}): OrderValues => {
  if (!variant) {
    return values
  }
  const packageVariantValues = {
    ...VariablesUtils.fromOverrides(variant.overrides),
    package: variant?.value,
    lensPrefix: variant?.lensPrefix,
  }

  return {
    refraction: values.refraction,
    values: {
      ...values.values,
      ...packageVariantValues,
      // We need to make sure the package overrides are applied before steps values
      ...getValuesFromSteps(fullState),
    },
  } as OrderValues
}

const getActiveOrderItem = ({
  items,
  activeItemId,
}: Pick<OrderState, 'items' | 'activeItemId'>) => {
  return items.find((item) => item.id === activeItemId)
}

type SummaryPriceValues = {
  priceTotal?: number
  priceTotalBase?: number
  priceTotalExtra?: number
  priceTotalCurrent?: number
  priceBaseBundle?: number
  priceExtraBundle?: number
  priceBaseItems?: number
  priceExtraItems?: number
}

const getSummaryValues = (
  summary: OrderPriceSummary,
  current: BundleType
): SummaryPriceValues => {
  const bundles = summary.items.filter((item) => item.item.summary === 'bundle')
  const baseBundle = bundles.find((item) => item.item.bundleType === 'base')
  const extraBundle = bundles.find((item) => item.item.bundleType === 'extra')

  const baseItems = summary.items.filter(
    (item) => item.item.summary === 'frame' && item.item.bundleType === 'base'
  )
  const extraItems = summary.items.filter(
    (item) => item.item.summary === 'frame' && item.item.bundleType === 'extra'
  )

  const result: SummaryPriceValues = {
    priceTotal: Discounts.unwrap(summary.price),
  }

  if (baseBundle) {
    result.priceBaseBundle = Discounts.unwrap(baseBundle.price)
  }

  if (extraBundle) {
    result.priceExtraBundle = Discounts.unwrap(extraBundle.price)
  }

  if (baseItems.length > 0) {
    const baseItemsPrice = baseItems.reduce(
      (acc, item) => acc + Discounts.unwrap(item.price),
      0
    )
    result.priceBaseItems = baseItemsPrice
  }

  if (extraItems.length > 0) {
    const extraItemsPrice = extraItems.reduce(
      (acc, item) => acc + Discounts.unwrap(item.price),
      0
    )
    result.priceExtraItems = extraItemsPrice
  }

  if (result.priceBaseBundle != null && result.priceBaseItems != null) {
    result.priceTotalBase = result.priceBaseBundle + result.priceBaseItems
  }

  if (result.priceExtraBundle != null && result.priceExtraItems != null) {
    result.priceTotalExtra = result.priceExtraBundle + result.priceExtraItems
  }

  if (current === 'base' && result.priceTotalBase != null) {
    result.priceTotalCurrent = result.priceTotalBase
  }
  if (current === 'extra' && result.priceTotalExtra != null) {
    result.priceTotalCurrent = result.priceTotalExtra
  }

  return result
}

export const getOrderValues = ({
  order,
  frame,
  fullState,
  defaults,
  bundle = 'base',
}: {
  order: OrderState
  frame?: OrderFrame
  fullState: StepsState
  defaults: VariableValues
  bundle?: BundleType
}) => {
  const packageValues = getPackageValues(order.packageCfg[bundle])

  const activeItem = getActiveOrderItem(order)
  const initialValues = VariablesUtils.merge(
    defaults,
    // Add values based on the current order state
    getPackageValues(order.packageCfg[bundle]),
    FrameUtils.getPayload(frame, packageValues.type || 'sv'),
    frame ? FrameUtils.getValues(frame) : {},
    getValuesFromSteps(fullState),
    {
      flow: activeItem?.flowName ?? DEFAULT_FLOW_NAME,
    }
  )

  return initialValues
}

export const withSummaryValues = (
  values: OrderValues,
  summary: OrderPriceSummary | null,
  current: BundleType
): OrderValues => {
  if (!summary) {
    return values
  }

  return {
    values: {
      ...values.values,
      ...getSummaryValues(summary, current),
    },
    refraction: values.refraction,
  }
}

export const getValuesDefaults = ({
  dealCode,
  dealType,
  frames,
  customer,
  store,
}: {
  dealCode: string | undefined
  dealType: DealType | null | undefined
  frames: number | undefined
  customer: Customer | undefined
  store: Store | undefined
}) => {
  const defaults: VariableValues = {
    region: getRemoteConfig()?.region.countryCode || getAppRegion().code,
    deal: dealCode,
    frames,
    age: customer ? getAge(customer.birth_date) : null,
    dealType,
    // For config conditions we want access to the store reference and id
    store_ref: store?.ref,
    store_id: store?.id,
    store_type: store?.type,
  }
  return defaults
}

const getCurrentDefaults = (bundleType: BundleType) => {
  const deals = getDealsById()
  const session = useSession.getState()
  const customer = session.customer?.data
  const store = session.store?.data

  const dealCtx = resolveSessionDeal(bundleType, session.deals)
  const deal = dealCtx ? deals[dealCtx?.dealId] : undefined
  return getValuesDefaults({
    dealCode: deal?.code,
    dealType: deal?.type ?? 'normal',
    frames: deal?.frames,
    customer,
    store,
  })
}

const resolveSteps = (states?: Partial<Record<StepMode, StepsState>>) => {
  if (!states) {
    return []
  }
  const { bundle, ...rest } = states

  return Object.values(rest).flat()
}

/**
 * Get the current order values for the given input
 *
 * This is on resolved on demand based on the latest state
 *
 * Use this in callbacks / function calls
 */
export const getCurrentOrderValues = (input: {
  states?: Partial<Record<StepMode, StepsState>>
  frame?: OrderFrame
  bundleType?: BundleType
}) => {
  const { order, steps } = useOrderState.getState()

  const active = getActiveOrderItem(order)
  const frame = input?.frame || active?.frame
  const bundleType = input?.bundleType || active?.bundleType || 'base'
  const prescriptionId = order.prescriptionId
  const session = useSession.getState()
  const customerId = session.customer?.data.id
  const refraction = getPrescriptionByIdFromCache(prescriptionId, customerId)
  const bundleState =
    input?.states?.bundle || order.bundles[bundleType]?.steps || []
  const itemsState = input?.states || steps[active?.id ?? '']

  const fullState = [...bundleState, ...resolveSteps(itemsState)]
  const defaults = getCurrentDefaults(bundleType)

  const res = {
    values: getOrderValues({
      order,
      frame,
      fullState,
      defaults,
      bundle: bundleType,
    }),
    refraction: applyRefractionActions(
      refraction,
      fullState,
      resolvePd(order.pd)
    ),
  }

  return res
}
